import type {
  PublicWarningSettings,
  StaticMapOverlaysOverlayConfiguration,
  UpdatePublicWarningSettingsBody,
} from "@/lib/api/types";
import { overrideOr, toOverride, type WarningPageCopy } from "@/lib/warning-page-copy";

export interface PublicWarningPageSettings extends WarningPageCopy {
  enabled: boolean;
  /** Static map overlays drawn on the risk picture map, in display order. */
  staticOverlays: StaticMapOverlaysOverlayConfiguration[];
}

/** Shared so absent settings yield a stable reference (overlay layers are memoized on it). */
const NO_OVERLAYS: StaticMapOverlaysOverlayConfiguration[] = [];

/** Resolves overrides over locale `defaults`; absent settings keep the page enabled. */
export function readPublicWarningPageSettings(
  settings: PublicWarningSettings | null | undefined,
  defaults: WarningPageCopy,
): PublicWarningPageSettings {
  return {
    enabled: settings?.enabled !== false,
    title: overrideOr(settings?.title_override, defaults.title),
    description: overrideOr(settings?.description_override, defaults.description),
    toolbarLabel: overrideOr(settings?.toolbar_label_override, defaults.toolbarLabel),
    staticOverlays: settings?.static_map_overlay_configurations ?? NO_OVERLAYS,
  };
}

/** Copy that is blank or equals the locale default is sent as `null` (no override). */
export function publicWarningSettingsUpdateBody(
  values: {
    enabled: boolean;
    title?: string;
    description?: string;
    toolbarLabel?: string;
    staticOverlays?: StaticMapOverlaysOverlayConfiguration[];
  },
  defaults: WarningPageCopy,
): UpdatePublicWarningSettingsBody {
  return {
    enabled: values.enabled,
    title_override: toOverride(values.title, defaults.title),
    description_override: toOverride(values.description, defaults.description),
    toolbar_label_override: toOverride(values.toolbarLabel, defaults.toolbarLabel),
    static_map_overlay_configurations: values.staticOverlays ?? [],
  };
}
